/**
 * Converts a store's barcodeSettings + a list of print-data rows into a
 * ZPL command string ready to stream to a thermal printer. Each ^XA...^XZ
 * block is one physical form on the label roll: barcodeSettings.NoOfColumns
 * items are laid out side by side (by X-offsetting each item's fields by
 * NoOfColumns * LabelWidth) within that block, matching multi-up die-cut
 * label stock where the printer only advances past a gap once per row.
 *
 * Mirrors the layout rules from the desktop printer app's
 * printDoc_PrintPage: fields marked FieldSelected with FieldPosition
 * "above barcode" / "below barcode" are drawn in FieldSequence order
 * around a Code128 barcode encoding the item's SKU.
 */

const DEFAULT_DPI = 203; // standard for most USB thermal label printers

function mmToDots(mm, dpi) {
  const value = Number(mm) || 0;
  return Math.round((value * dpi) / 25.4);
}

function escapeZpl(text) {
  // ^ ~ and \ are meaningful to ZPL and must be escaped out of field data.
  // These hex escapes are only decoded when the field is preceded by ^FH\
  // (without it the printer prints "\5E" literally).
  return String(text == null ? "" : text)
    .replace(/\\/g, "\\5C")
    .replace(/\^/g, "\\5E")
    .replace(/~/g, "\\7E");
}

// Caps how many lines a single field's ^FB block can wrap onto, so one
// pathologically long value can't push everything else off the label.
const MAX_TEXT_LINES = 4;

// The old desktop app measured the actual rendered string width (GDI
// MeasureString) and only reserved extra height when a field truly didn't
// fit on one line — short fields (price, MRP) stayed single-line, only long
// ones (name) grew. We can't measure ZPL's built-in font that precisely, but
// simulating the same greedy word-wrap ^FB actually does gets close: a line
// only breaks when a whole word doesn't fit, not at a fixed character
// count. (An earlier char-count/line version badly overestimated — it
// assumed text splits evenly across lines, when in reality ZPL packs nearly
// everything onto line 1 and overflows only the last word or two. That
// overshoot reserved so much extra height that later fields, e.g. MRP,
// were pushed past the label's bottom edge and vanished off the print.)
//
// ^A0 (CG Triumvirate Bold Condensed) is proportional, so a flat
// per-character width can't be right for both mixed-case and ALL-CAPS text:
// an earlier flat 0.45 * fontWidthDots factor, calibrated on mixed-case
// text, underestimated all-caps names (~30% wider) — e.g. Silvertex's
// "AC COMFORTOR SET - STAR - 4 PC SET" was estimated at 1 line but printed
// on 2, and ^FB overprints whatever exceeds its line count onto the last
// line, garbling it ("SET" stamped over "AC "). Glyph advances below are
// Helvetica-Bold widths (per 1000 em, same proportions as Triumvirate),
// scaled by GLYPH_WIDTH_SCALE — fitted against two real prints:
//  - Max Shoppee, 1.4mm bold, 34mm label, 3mm margin: "Vicks 3 In 1 Honey
//    Lemon Menthol Lozenges 8" fit on line 1, only "PIECE" wrapped
//    (allows 0.75–0.86).
//  - Silvertex: "AC COMFORTOR SET - ULTRASONIC 4" / "AC COMFORTOR SET -
//    STAR - 4 PC" fit on line 1, the next word wrapped (allows ~0.79–1.0).
const GLYPH_WIDTH_SCALE = 0.82;
const DEFAULT_GLYPH_WIDTH = 611; // unlisted chars (accented, non-Latin, etc.)
const GLYPH_WIDTHS = (() => {
  const widths = {
    " ": 278, "!": 333, '"': 474, "#": 556, $: 556, "%": 889, "&": 722,
    "'": 238, "(": 333, ")": 333, "*": 389, "+": 584, ",": 278, "-": 333,
    ".": 278, "/": 278, ":": 333, ";": 333, "<": 584, "=": 584, ">": 584,
    "?": 611, "@": 975, "[": 333, "\\": 278, "]": 333, "^": 584, _: 556,
    "`": 333, "{": 389, "|": 280, "}": 389, "~": 584,
  };
  const upper = [722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833,
    722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611];
  const lower = [556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889,
    611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500];
  for (let i = 0; i < 26; i++) {
    widths[String.fromCharCode(65 + i)] = upper[i];
    widths[String.fromCharCode(97 + i)] = lower[i];
  }
  for (let d = 0; d <= 9; d++) widths[String(d)] = 556;
  return widths;
})();

function textWidthDots(text, fontWidthDots) {
  let em = 0;
  for (const ch of text) em += GLYPH_WIDTHS[ch] || DEFAULT_GLYPH_WIDTH;
  return (em / 1000) * fontWidthDots * GLYPH_WIDTH_SCALE;
}

function estimateLineCount(text, fontWidthDots, maxWidthDots) {
  if (!text || maxWidthDots <= 0 || fontWidthDots <= 0) return 1;

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;

  const spaceWidth = textWidthDots(" ", fontWidthDots);
  let lines = 1;
  let currentLineWidth = 0;
  words.forEach((word) => {
    const wordWidth = textWidthDots(word, fontWidthDots);
    const candidateWidth =
      currentLineWidth === 0 ? wordWidth : currentLineWidth + spaceWidth + wordWidth;
    if (candidateWidth > maxWidthDots && currentLineWidth > 0) {
      lines++;
      currentLineWidth = wordWidth;
    } else {
      currentLineWidth = candidateWidth;
    }
    // A single word wider than the line gets broken mid-word by ^FB.
    while (currentLineWidth > maxWidthDots) {
      lines++;
      currentLineWidth -= maxWidthDots;
    }
  });

  return Math.min(MAX_TEXT_LINES, lines);
}

function buildTextField(text, xDots, yDots, fontHeightDots, fontWidthDots, maxWidthDots, lines) {
  let zpl = `^FO${xDots},${yDots}^A0N,${fontHeightDots},${fontWidthDots}`;
  if (maxWidthDots > 0) {
    // One spare line beyond the estimate: ^FB overprints any overflow onto
    // its last line, so if the estimate is ever one short the text should
    // wrap onto a fresh line rather than garble the one above. The spare
    // line costs nothing when unused — y still advances by `lines` only.
    const blockLines = Math.min(MAX_TEXT_LINES, lines + 1);
    zpl += `^FB${maxWidthDots},${blockLines},0,L,0`;
  }
  zpl += `^FH\\^FD${escapeZpl(text)}^FS\n`;
  return zpl;
}

// Code128's worst-case width (subset B, one symbol per character — no
// digit-pair compression): 11 dots/module * (chars + start + checksum) +
// 13 dots for the stop character. The real barcode can only be this wide or
// narrower (subset C compresses runs of digits), so sizing against this
// upper bound guarantees it always fits — never shrinks more than needed
// in the common case, but never risks printing wider than the label either.
function fitBarWidthScale(skuText, configuredScale, maxWidthDots) {
  const numChars = String(skuText == null ? "" : skuText).length;
  if (numChars === 0 || maxWidthDots <= 0) return configuredScale;
  const worstCaseModules = 11 * (numChars + 2) + 13;
  const fittingScale = Math.floor(maxWidthDots / worstCaseModules);
  return Math.max(1, Math.min(configuredScale, fittingScale || 1));
}

function getOrderedFields(printFields, position) {
  return (printFields || [])
    .filter(
      (f) => f.FieldSelected && (f.FieldPosition || "").toLowerCase() === position
    )
    .sort((a, b) => (a.FieldSequence || 0) - (b.FieldSequence || 0));
}

// barcodeSettings.PrintFields uses human-readable FieldName labels (e.g.
// "Retail Price") that don't match the PascalCase keys on the print-data
// objects built in BarcodePrint.js (e.g. RetailPrice) — map the ones that
// differ; fields not listed here already match their product key as-is.
const FIELD_NAME_TO_PRODUCT_KEY = {
  "Retail Price": "RetailPrice",
  "Supply Price": "SupplyPrice",
  "Sub Category": "SubCategory",
  "Vendor Code": "VendorCode",
  "Vendor Name": "VendorName",
  "Store Code": "StoreCode",
  "Store Name": "StoreName",
  "Transaction No": "TransactionNo",
  "Transaction Date": "TransactionDate",
};

function fieldValueFor(product, field, noOfCharacter) {
  const key = FIELD_NAME_TO_PRODUCT_KEY[field.FieldName] || field.FieldName;
  const raw = product && product.hasOwnProperty(key) ? product[key] : "";
  const displayName = field.FieldDisplayName || "";
  let text = displayName + (raw === null || raw === undefined ? "" : raw);

  if (noOfCharacter && text.length > noOfCharacter) {
    text = text.substring(0, noOfCharacter);
  }
  return text;
}

// Builds the field commands for one item's label content, offset xOffsetDots
// to the right so several items can share one physical form side by side.
function buildLabelFields(barcodeSettings, product, dpi, xOffsetDots, labelWidthDots) {
  const localLeftMarginDots = mmToDots(barcodeSettings.LeftMarginInsideLabel, dpi);
  const leftMarginDots = localLeftMarginDots + xOffsetDots;
  // Matches the desktop app's ActualAvailableWidth = LabelWidth - LeftMarginBox
  // (margin subtracted once, not on both sides).
  const maxTextWidthDots = Math.max(1, labelWidthDots - localLeftMarginDots);
  const topMarginDots = mmToDots(barcodeSettings.TopMarginInsideLabel, dpi);
  const barcodeHeightDots =
    mmToDots(barcodeSettings.BarcodeHeight, dpi) || mmToDots(10, dpi);
  const barWidthScale = Math.min(
    10,
    Math.max(1, Math.round(barcodeSettings.BarWidthScale) || 2)
  );
  const noOfCharacter = barcodeSettings.NoOfCharacter || 100;
  // The desktop app declared MarginBetweenField as a per-store setting but
  // never actually read it, always using a hardcoded 1mm instead — honor
  // the real setting here, falling back to that same 1mm default only when
  // it's genuinely unset (not just 0 — a store can deliberately want no
  // gap, and `0 || fallback` would wrongly override that since 0 is falsy).
  const lineGapDots =
    barcodeSettings.MarginBetweenField != null
      ? mmToDots(barcodeSettings.MarginBetweenField, dpi)
      : mmToDots(1, dpi);
  const defaultFontDots = mmToDots(3, dpi);

  const aboveFields = getOrderedFields(barcodeSettings.PrintFields, "above barcode");
  const belowFields = getOrderedFields(barcodeSettings.PrintFields, "below barcode");

  let y = topMarginDots;
  let zpl = "";

  const drawFields = (fields) => {
    fields.forEach((field) => {
      const fontHeightDots = mmToDots(field.FieldFontSize, dpi) || defaultFontDots;
      const bold = (field.FieldFontStyle || "").toLowerCase() === "bold";
      const fontWidthDots = bold
        ? Math.round(fontHeightDots * 1.15)
        : fontHeightDots;
      const text = fieldValueFor(product, field, noOfCharacter);
      const lines = estimateLineCount(text, fontWidthDots, maxTextWidthDots);
      zpl += buildTextField(
        text,
        leftMarginDots,
        y,
        fontHeightDots,
        fontWidthDots,
        maxTextWidthDots,
        lines
      );
      y += fontHeightDots * lines + lineGapDots;
    });
  };

  drawFields(aboveFields);

  // Barcode encodes SKU, matching the existing printer app's behaviour.
  // Shrunk (never grown) so it can't overflow the label like the old app's
  // ActualAvailableWidth check did for its barcode image.
  const fittedBarWidthScale = fitBarWidthScale(
    product.SKU,
    barWidthScale,
    maxTextWidthDots
  );
  zpl += `^BY${fittedBarWidthScale}\n`;
  zpl += `^FO${leftMarginDots},${y}`;
  zpl += `^BCN,${barcodeHeightDots},N,N,N\n`;
  zpl += `^FH\\^FD${escapeZpl(product.SKU)}^FS\n`;
  y += barcodeHeightDots + lineGapDots;

  drawFields(belowFields);

  return zpl;
}

/**
 * Builds ZPL for every item in printDataList, repeated NoOfPrints times each,
 * laid out NoOfColumns-across per physical form. Returns "" if there's
 * nothing to print.
 */
export function generateBarcodeCommands(barcodeSettings, printDataList, dpi) {
  if (!barcodeSettings) {
    throw new Error("Barcode settings are not loaded.");
  }
  if (!printDataList || printDataList.length === 0) {
    return "";
  }

  // Different customers run different printer models — DPI is a per-store
  // setting (barcodeSettings.PrinterDPI) rather than a fixed assumption,
  // since guessing wrong compresses every size (fonts, margins, ^LL label
  // length) the same way the old GDI-driver printing never had to worry
  // about, because the driver auto-scaled to the printer's real DPI.
  dpi = dpi || Number(barcodeSettings.PrinterDPI) || DEFAULT_DPI;

  const labelWidthDots = mmToDots(barcodeSettings.LabelWidth, dpi);
  const labelHeightDots = mmToDots(barcodeSettings.LabelHeight, dpi);
  const horizontalSpaceDots = mmToDots(barcodeSettings.HorizontalSpace, dpi);
  const columnPitchDots = labelWidthDots + horizontalSpaceDots;
  const columns = Math.max(1, Number(barcodeSettings.NoOfColumns) || 1);

  const units = [];
  printDataList.forEach((product) => {
    const copies = Math.max(1, Number(product.NoOfPrints) || 1);
    for (let i = 0; i < copies; i++) units.push(product);
  });

  let zpl = "";
  for (let i = 0; i < units.length; i += columns) {
    const rowItems = units.slice(i, i + columns);
    zpl += "^XA\n^CI28\n";
    if (labelWidthDots > 0) {
      zpl += `^PW${columnPitchDots * columns - horizontalSpaceDots}\n`;
    }
    if (labelHeightDots > 0) zpl += `^LL${labelHeightDots}\n`;
    for (let col = 0; col < rowItems.length; col++) {
      zpl += buildLabelFields(
        barcodeSettings,
        rowItems[col],
        dpi,
        col * columnPitchDots,
        labelWidthDots
      );
    }
    zpl += "^XZ\n";
  }

  return zpl;
}
